// Community Service
// API service layer for community operations

import { Post, Comment, SortOption, CommunityInfo } from '../../types/community';
import { sortPosts } from '../../utils/communitySorting';
import { config } from '../../config/env';
import { authInterceptor } from '../Authentication/authInterceptor';

const API_BASE = `${config.api.baseUrl}/communities`;

function headers(json = true): HeadersInit {
  const h: HeadersInit = { Authorization: `Bearer ${authInterceptor.getAccessToken() || ''}` };
  if (json) h['Content-Type'] = 'application/json';
  return h;
}

async function unwrap(response: Response): Promise<any> {
  if (!response.ok) {
    // The backend's message, not the status text. A rejected link and a
    // Premium Plus requirement both arrive here, and both are things the user
    // can act on — 'Request failed: Forbidden' tells them nothing.
    let message = `Request failed: ${response.statusText}`;
    let code: string | undefined;
    try {
      const failure = await response.json();
      message = failure?.message || failure?.error?.message || message;
      code = failure?.error?.code ?? failure?.code ?? failure?.error;
    } catch {
      // Non-JSON body: keep the status text.
    }
    const error = new Error(message) as Error & { code?: string; status?: number };
    error.code = typeof code === 'string' ? code : undefined;
    error.status = response.status;
    throw error;
  }
  const body = await response.json();
  return body.data;
}

function toPost(row: any): Post {
  return {
    id: row.id,
    communityId: row.community_id,
    title: row.title,
    body: row.body,
    authorId: row.author_id,
    authorName: row.author?.first_name ?? 'Unknown',
    createdAt: new Date(row.created_at),
    updatedAt: row.updated_at ? new Date(row.updated_at) : undefined,
    upvotes: row.upvotes ?? 0,
    downvotes: row.downvotes ?? 0,
    commentCount: row.comment_count ?? 0,
    userVote: row.userVote ?? null,
    isSaved: false,
  };
}

function toCommunityInfo(row: any): CommunityInfo {
  return {
    id: row.id,
    slug: row.slug,
    name: row.name,
    description: row.description,
    icon: row.icon,
    type: row.type,
    memberCount: row.memberCount ?? 0,
  };
}

function toComment(row: any): Comment {
  return {
    id: row.id,
    postId: row.post_id,
    parentCommentId: row.parent_comment_id ?? null,
    body: row.body,
    authorId: row.author_id,
    authorName: row.author?.first_name ?? 'Unknown',
    createdAt: new Date(row.created_at),
    updatedAt: row.updated_at ? new Date(row.updated_at) : undefined,
    upvotes: row.upvotes ?? 0,
    downvotes: row.downvotes ?? 0,
    userVote: row.userVote ?? null,
  };
}

export class CommunityService {
  /**
   * Get posts for a community with sorting
   */
  static async getCommunityPosts(communityId: string, sort: SortOption): Promise<Post[]> {
    const response = await fetch(`${API_BASE}/${communityId}/posts`, { headers: headers(false) });
    const data = await unwrap(response);
    const posts = (data ?? []).map(toPost);
    return sortPosts(posts, sort);
  }

  /**
   * Get single post details
   */
  static async getPost(postId: string, communityId: string): Promise<Post | null> {
    const response = await fetch(`${API_BASE}/posts/${postId}`, { headers: headers(false) });
    const data = await unwrap(response);
    return data ? toPost(data) : null;
  }

  /**
   * Get comments for a post
   */
  static async getPostComments(postId: string): Promise<Comment[]> {
    const response = await fetch(`${API_BASE}/posts/${postId}/comments`, { headers: headers(false) });
    const data = await unwrap(response);
    return (data ?? []).map(toComment);
  }

  /**
   * Create a new post — text-only, chat rooms don't support media/tags.
   */
  static async createPost(communityId: string, title: string, body: string): Promise<Post> {
    const response = await fetch(`${API_BASE}/${communityId}/posts`, {
      method: 'POST',
      headers: headers(),
      body: JSON.stringify({ title, body }),
    });
    const data = await unwrap(response);
    return toPost(data);
  }

  /**
   * Create a new comment, or a reply when parentCommentId is given.
   */
  static async createComment(postId: string, body: string, parentCommentId?: string): Promise<Comment> {
    const response = await fetch(`${API_BASE}/posts/${postId}/comments`, {
      method: 'POST',
      headers: headers(),
      body: JSON.stringify({ body, parentCommentId }),
    });
    const data = await unwrap(response);
    return toComment(data);
  }

  /**
   * Vote on a post
   */
  static async voteOnPost(postId: string, voteType: 'upvote' | 'downvote'): Promise<void> {
    const response = await fetch(`${API_BASE}/posts/${postId}/vote`, {
      method: 'POST',
      headers: headers(),
      body: JSON.stringify({ voteType }),
    });
    await unwrap(response);
  }

  /**
   * Vote on a comment
   */
  static async voteOnComment(commentId: string, voteType: 'upvote' | 'downvote'): Promise<void> {
    const response = await fetch(`${API_BASE}/comments/${commentId}/vote`, {
      method: 'POST',
      headers: headers(),
      body: JSON.stringify({ voteType }),
    });
    await unwrap(response);
  }

  /**
   * Save/unsave a post
   */
  static async savePost(postId: string): Promise<void> {
    const response = await fetch(`${API_BASE}/posts/${postId}/save`, {
      method: 'POST',
      headers: headers(),
    });
    await unwrap(response);
  }

  /**
   * Get community information
   */
  static async getCommunityInfo(communityId: string, type: 'persona' | 'custom'): Promise<CommunityInfo> {
    const response = await fetch(`${API_BASE}/${communityId}`, { headers: headers(false) });
    const data = await unwrap(response);
    return toCommunityInfo(data);
  }

  /**
   * List every community (persona rooms + user-created custom rooms), with
   * real member counts.
   */
  static async getAllCommunities(): Promise<CommunityInfo[]> {
    const response = await fetch(API_BASE, { headers: headers(false) });
    const data = await unwrap(response);
    return (data ?? []).map(toCommunityInfo);
  }

  /**
   * Create a new custom chat room. The creator is auto-joined as its first
   * member.
   */
  static async createCommunity(name: string, description: string, icon: string): Promise<CommunityInfo> {
    const response = await fetch(API_BASE, {
      method: 'POST',
      headers: headers(),
      body: JSON.stringify({ name, description, icon }),
    });
    const data = await unwrap(response);
    return toCommunityInfo(data);
  }
}
